import { Dialog } from '@progress/kendo-react-dialogs';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import type { GridCellProps } from '@progress/kendo-react-grid';
import { Button, Chip } from '@progress/kendo-react-buttons';
import type { ChipProps } from '@progress/kendo-react-buttons';
import { useFormBuilder } from '../context/FormBuilderContext';
import type { FormVersion, FormVersionStatus } from '../types';

const STATUS_THEME: Record<FormVersionStatus, ChipProps['themeColor']> = {
  draft: 'warning',
  published: 'success',
  archived: 'base',
};

function StatusCell(props: GridCellProps) {
  const dataItem = props.dataItem as FormVersion;
  return (
    <td className={props.className} style={props.style} role="gridcell" aria-colindex={props.ariaColumnIndex}>
      <Chip text={dataItem.status} themeColor={STATUS_THEME[dataItem.status]} />
    </td>
  );
}

export function VersionPanel({ onClose }: { onClose: () => void }) {
  const { versions, activeVersion, publish, restoreVersion, archiveVersion } = useFormBuilder();

  function ActionsCell(props: GridCellProps) {
    const dataItem = props.dataItem as FormVersion;
    const isCurrent = dataItem.formVersionId === activeVersion.formVersionId;
    return (
      <td className={props.className} style={props.style} role="gridcell" aria-colindex={props.ariaColumnIndex}>
        <div style={{ display: 'flex', gap: 'var(--kendo-spacing-1)' }}>
          {dataItem.status === 'draft' && (
            <Button size="small" themeColor="primary" onClick={() => publish()} disabled={!isCurrent}>
              Publish
            </Button>
          )}
          {dataItem.status !== 'draft' && (
            <Button size="small" onClick={() => restoreVersion(dataItem.formVersionId)}>
              Restore as Draft
            </Button>
          )}
          {dataItem.status === 'published' && (
            <Button size="small" onClick={() => archiveVersion(dataItem.formVersionId)}>
              Archive
            </Button>
          )}
        </div>
      </td>
    );
  }

  const sorted = [...versions].sort((a, b) => b.versionNumber - a.versionNumber);

  return (
    <Dialog title="Version History" onClose={onClose} width={700}>
      <Grid data={sorted} scrollable="none">
        <GridColumn field="versionNumber" title="Version" width={80} />
        <GridColumn field="status" title="Status" width={110} cells={{ data: StatusCell }} />
        <GridColumn field="createdBy" title="Created By" width={140} />
        <GridColumn field="createdAt" title="Created" width={170} />
        <GridColumn title="Actions" cells={{ data: ActionsCell }} />
      </Grid>
    </Dialog>
  );
}
